/**
 * AreaChartControl
 *
 * Area chart with filled regions
 * Supports stacked and grouped areas
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData, getSeriesConfig } from './BaseChartComponent';

const AreaChartControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);
  const seriesConfig = useMemo(() => getSeriesConfig(control), [control]);

  const areaConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      stacking: (config?.stacking as 'none' | 'positive') || 'none',
      interpolation: (config?.interpolation as 'linear' | 'spline') || 'linear',
      showDots: config?.showDots !== false,
    };
  }, [control.config]);

  const renderArea = useMemo(() => {
    if (!chartData || chartData.length === 0 || seriesConfig.length === 0) return null;

    const padding = 40;
    const width = 600;
    const height = (control.config?.height as number) || 400;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    const allValues: number[] = [];
    chartData.forEach((d) => {
      seriesConfig.forEach((s) => {
        const val = (d as Record<string, unknown>)[s.dataKey];
        if (typeof val === 'number') allValues.push(val);
      });
    });

    const maxValue = Math.max(...allValues, 1);
    const xScale = plotWidth / Math.max(chartData.length - 1, 1);
    const yScale = plotHeight / maxValue;

    const getPoint = (dataIndex: number, seriesIndex: number): [number, number] | null => {
      const value = (chartData[dataIndex] as Record<string, unknown>)[
        seriesConfig[seriesIndex].dataKey
      ];
      if (typeof value !== 'number') return null;
      const x = padding + dataIndex * xScale;
      const y = height - padding - value * yScale;
      return [x, y];
    };

    const createAreaPath = (seriesIndex: number): string => {
      const points = chartData
        .map((_, i) => getPoint(i, seriesIndex))
        .filter(Boolean) as [number, number][];
      if (points.length === 0) return '';

      let path = `M ${points[0][0]} ${height - padding}`;
      points.forEach((p) => (path += ` L ${p[0]} ${p[1]}`));
      path += ` L ${points[points.length - 1][0]} ${height - padding}`;
      return path;
    };

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - padding - ratio * plotHeight;
          return (
            <line
              key={`grid-${ratio}`}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}

        {/* Axes */}
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#000" />
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#000" />

        {/* Areas */}
        {seriesConfig.map((series, idx) => (
          <path
            key={`area-${series.id}`}
            d={createAreaPath(idx)}
            fill={series.color || '#8884d8'}
            opacity="0.5"
            stroke={series.color || '#8884d8'}
            strokeWidth="2"
          />
        ))}

        {/* Legend */}
        {(control.config?.legend as Record<string, unknown> | undefined)?.visible !== false && (
          <g>
            {seriesConfig.map((series, index) => {
              const legendX = width - padding - 150;
              const legendY = padding + index * 25;
              return (
                <g key={`legend-${series.id}`}>
                  <rect
                    x={legendX}
                    y={legendY}
                    width={16}
                    height={16}
                    fill={series.color || '#8884d8'}
                    opacity="0.5"
                  />
                  <text x={legendX + 20} y={legendY + 13} fontSize="12" fill="#374151">
                    {series.name}
                  </text>
                </g>
              );
            })}
          </g>
        )}
      </svg>
    );
  }, [chartData, seriesConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="area-chart">
      <div style={{ width: '100%', height: '100%' }}>{renderArea}</div>
    </BaseChartComponent>
  );
});

AreaChartControl.displayName = 'AreaChartControl';
export default AreaChartControl;
